// © 2025–2026 Avv. Dr. Gian Marco Solas · All rights reserved
// Corporate Disclosure Scanner · corporate-scanner.com
// Theory of Interrelation · Legal-physics framework
// Published: JLMI 1/25 (DOI 10.13135/2785-7867/12266)
// Pre-print: "Law is Love" — SSRN 5694423 (physics-reviewed)
// Book: De Lege et Amore (ISBN 9791221030631)
// Unauthorised reproduction, adaptation or commercial use is prohibited.
// Central API helper — two-call pipeline for reliable, complete analysis

// ── Core fetch ────────────────────────────────────────────────────────────────
export async function callAPI(body) {
  const res = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ max_tokens: 6000, ...body }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || JSON.stringify(data.error));
  return (data.content || []).filter(b => b.type === 'text').map(b => b.text).join('').trim();
}

// ── Call 1: EXTRACT ───────────────────────────────────────────────────────────
// Read the full document, pull the most relevant passages per anomaly category.
// Returns structured excerpts only — no analysis, no scoring.
const EXTRACT_PROMPT = `You are a document analyst. Read the corporate disclosure and extract the most relevant passages for each anomaly category below.

For each category, extract 1-2 short passages (max 40 words each) that are most relevant — or write "NONE FOUND" if nothing relevant exists.

For COMPETITION LAW look specifically for: antitrust investigations, cartel, leniency, follow-on damages, private enforcement claims, collective redress, NCA decisions, dominance, merger control.

For PRIVATE CLAIMS look for: shareholder claims, product liability, environmental damage, GDPR compensation claims, class actions, group litigation, IAS 37 provisions, supply chain liability, human rights litigation, undisclosed legal proceedings, contingent liabilities.

Return ONLY this exact JSON, no prose, no markdown:
{
  "GOVERNANCE": ["passage1", "passage2"],
  "FINANCIAL FRAUD": ["passage1"],
  "MARKET ABUSE": ["NONE FOUND"],
  "ESG MISREPORTING": ["passage1"],
  "OPACITY": ["passage1", "passage2"],
  "RELATED PARTY": ["NONE FOUND"],
  "INSIDER DEALING": ["NONE FOUND"],
  "AUDITOR FAILURE": ["passage1"],
  "COMPETITION LAW": ["NONE FOUND"],
  "DATA PROTECTION": ["NONE FOUND"],
  "PRIVATE CLAIMS": ["NONE FOUND"]
}`;

// ── Call 2: ANALYSE ───────────────────────────────────────────────────────────
// Work only from extracted passages. Calculate all scores, write anomalies.
// Input is small and controlled — no truncation possible.
function buildAnalysePrompt(juris, extractions, meta) {
  return `You are a senior corporate governance expert, forensic accountant, and securities law analyst covering EU (ESMA, MiFID II, MAR, CSRD), US (SEC, SOX, Dodd-Frank), UK (FCA, Companies Act 2006), and IOSCO/IFRS global standards.

You apply the legal-physics framework: entropy = unexpressed/wasted value (S=k·logW(λ)), inertia = governance ossification, motive_power = productive legal-economic work (Carnot cycle PC→λC→λS), legal_weight = regulatory friction (PC=λCF), investability = π=λ(R−C).

Jurisdiction: ${juris || 'EU / US / UK / Global'}.
IMPORTANT: Identify ALL jurisdictions applicable to this entity from the document — listed exchange, country of incorporation, operating jurisdictions, regulatory frameworks referenced. Apply the standards of ALL relevant jurisdictions. A company listed on multiple exchanges or operating across borders must be assessed against each applicable framework.
${meta ? `Document metadata: ${meta}` : ''}

SCORING RUBRIC — apply consistently:

ENTROPY (unexpressed/destroyed value):
85-100: Systemic opacity, multiple material misstatements, active concealment of liabilities
65-84: Significant gaps in 2+ categories, material omissions affecting investor decisions  
45-64: Isolated disclosure gaps, some boilerplate replacing substance
25-44: Minor omissions only, generally transparent
0-24: Full transparency, no material gaps detected

INERTIA (governance ossification):
85-100: Entrenched board, no independence, staggered defences, auditor >15 years
65-84: Significant governance lag, limited independent oversight, slow to adapt
45-64: Some structural rigidity, partial compliance with governance codes
25-44: Minor governance weaknesses, generally responsive structures
0-24: Strong independent governance, adaptive structures

MOTIVE POWER (productive legal-economic work):
75-100: Disclosure genuinely reflects real value creation and productive use of capital
50-74: Partial value creation visible, some productive signals
25-49: Limited productive output evident in disclosure
0-24: Disclosure conceals more than it reveals

LEGAL WEIGHT (regulatory friction):
75-100: Heavy unquantified regulatory burden, compliance costs dominate, W=MG(λ) very high
50-74: Moderate regulatory friction visible or notably absent from accounts
25-49: Manageable legal weight, compliance costs proportionate
0-24: Lean structure, low regulatory friction

INVESTABILITY (π=λ(R−C)):
75-100: Strong SDG alignment, codified asset value, sustainable return signal clear
50-74: Partial sustainability alignment, some real value codified
25-49: Bi-dimensional accounting dominates, limited sustainable signal
0-24: Commoditised disclosure, real value hidden or destroyed

EXTRACTED PASSAGES:
${Object.entries(extractions).map(([cat, passages]) =>
  `${cat}: ${Array.isArray(passages) ? passages.join(' | ') : passages}`
).join('\n')}

Based on these passages and the rubric above, return ONLY this exact JSON:

{"overall_verdict":"CRITICAL"|"SIGNIFICANT ANOMALIES"|"MINOR IRREGULARITIES"|"COMPLIANT","risk_score":0-100,"entropy_score":0-100,"inertia_score":0-100,"motive_power":0-100,"legal_weight_score":0-100,"investability_score":0-100,"pressure_source":"state_pressure"|"client_pressure"|"voluntary","erga_omnes_effect":"1 sentence","entropy_analysis":"1 sentence","inertia_analysis":"1 sentence","summary":"2-3 sentences","applicable_standards":["string"],"anomalies":[{"title":"≤6 words","category":"GOVERNANCE"|"FINANCIAL FRAUD"|"MARKET ABUSE"|"ESG MISREPORTING"|"OPACITY"|"RELATED PARTY"|"INSIDER DEALING"|"AUDITOR FAILURE"|"COMPETITION LAW"|"DATA PROTECTION"|"PRIVATE CLAIMS","severity":"high"|"medium"|"low","entropy_contribution":0-100,"inertia_contribution":0-100,"standard_violated":"string","doc_section":"string","excerpt":"≤20 words from passages above","legal_analysis":"1 sentence","norms_violated":["string"],"consequence":"1 sentence","erga_omnes":"1 sentence","responsibility":{"signatory":{"role":"string","name":null,"liability_type":"string","description":"1 sentence"},"internal_controller":{"roles":["string"],"legal_obligation":"string","description":"1 sentence"},"external_oversight":{"bodies":["string"],"action":"string","deadline":"string","description":"1 sentence"}}}],"positive_points":["string"],"recommendations":"2-3 sentences"}

RULES:
- Apply the rubric strictly — scores must be consistent across runs of the same document
- Only flag anomalies where relevant passages were found (not NONE FOUND)
- Max 5 anomalies, ranked by severity
- Start with { and end with } — complete JSON only`;
}

// ── Two-call pipeline ─────────────────────────────────────────────────────────
export async function runAnalysis({ messages, juris, meta, onProgress }) {
  // Call 1: Extract passages
  if (onProgress) onProgress('Extracting relevant passages…');
  
  const extractRaw = await callAPI({
    model: 'claude-sonnet-4-6',
    max_tokens: 2000,
    system: EXTRACT_PROMPT,
    messages,
  });

  let extractions = {};
  try {
    const clean = extractRaw.replace(/^```json\s*/i, '').replace(/```\s*$/i, '').trim();
    extractions = JSON.parse(clean);
  } catch {
    // If extraction fails, use a fallback single-call approach
    if (onProgress) onProgress('Analysing disclosure…');
    return await runSingleCall({ messages, juris, meta });
  }

  // Call 2: Analyse from extractions
  if (onProgress) onProgress('Calculating legal-physics scores…');

  const analyseRaw = await callAPI({
    model: 'claude-sonnet-4-6',
    max_tokens: 6000,
    system: buildAnalysePrompt(juris, extractions, meta),
    messages: [{ role: 'user', content: 'Analyse the extracted passages and return the complete JSON.' }],
  });

  return analyseRaw;
}

// ── Fallback single-call (for crawl mode / when extraction fails) ─────────────
const SINGLE_PROMPT = (juris) => `You are a senior corporate governance expert and securities law analyst covering EU (ESMA, MiFID II, MAR, CSRD), US (SEC, SOX, Dodd-Frank), UK (FCA, Companies Act 2006), and IOSCO/IFRS standards.

Apply the legal-physics framework: entropy=S=k·logW(λ), inertia=governance ossification, motive_power=Carnot useful effect, legal_weight=PC=λCF friction, investability=π=λ(R−C).

Jurisdiction: ${juris || 'EU / US / UK / Global'}.
Identify ALL jurisdictions applicable from the document and apply standards of each relevant framework.

SCORING RUBRIC:
ENTROPY: 85-100=systemic opacity/misstatements | 65-84=significant gaps 2+ categories | 45-64=isolated gaps | 25-44=minor omissions | 0-24=full transparency
INERTIA: 85-100=entrenched board/no independence | 65-84=significant governance lag | 45-64=some rigidity | 25-44=minor weaknesses | 0-24=strong governance
MOTIVE POWER: 75-100=genuine value creation visible | 50-74=partial | 25-49=limited | 0-24=conceals more than reveals
LEGAL WEIGHT: 75-100=heavy unquantified burden | 50-74=moderate friction | 25-49=proportionate | 0-24=lean structure
INVESTABILITY: 75-100=strong SDG/sustainable signal | 50-74=partial alignment | 25-49=bi-dimensional dominant | 0-24=commoditised

Apply rubric strictly for consistency across runs. Return ONLY complete valid JSON:

{"overall_verdict":"CRITICAL"|"SIGNIFICANT ANOMALIES"|"MINOR IRREGULARITIES"|"COMPLIANT","risk_score":1-100,"entropy_score":1-100,"inertia_score":1-100,"motive_power":1-100,"legal_weight_score":1-100,"investability_score":1-100,"pressure_source":"state_pressure"|"client_pressure"|"voluntary","erga_omnes_effect":"1 sentence","entropy_analysis":"1 sentence","inertia_analysis":"1 sentence","summary":"2 sentences","applicable_standards":["string"],"anomalies":[{"title":"≤6 words","category":"GOVERNANCE","severity":"high"|"medium"|"low","entropy_contribution":1-100,"inertia_contribution":1-100,"standard_violated":"string","doc_section":"string","excerpt":"≤20 words","legal_analysis":"1 sentence","norms_violated":["string"],"consequence":"1 sentence","erga_omnes":"1 sentence","responsibility":{"signatory":{"role":"string","name":null,"liability_type":"string","description":"1 sentence"},"internal_controller":{"roles":["string"],"legal_obligation":"string","description":"1 sentence"},"external_oversight":{"bodies":["string"],"action":"string","deadline":"string","description":"1 sentence"}}}],"positive_points":["string"],"recommendations":"2 sentences"}`;

export async function runSingleCall({ messages, juris, meta }) {
  const system = SINGLE_PROMPT(juris) + (meta ? `\n\nDocument metadata: ${meta}` : '');
  return await callAPI({
    model: 'claude-sonnet-4-6',
    max_tokens: 6000,
    system,
    messages,
  });
}

// ── Storage ───────────────────────────────────────────────────────────────────
export async function storeScan(entry) {
  try {
    const res = await fetch('/api/store-scan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entry),
    });
    const data = await res.json();
    if (!data.ok) console.warn('Scan storage failed:', data.error);
  } catch (e) {
    console.warn('Scan storage error:', e.message);
  }
}

export function buildScanEntry(result, meta, company, docType, juris, shareData) {
  return {
    id:            Date.now() + Math.random().toString(36).slice(2, 7),
    ts:            new Date().toISOString(),
    company:       company   || meta || 'Unknown',
    doc_type:      docType   || 'Unknown',
    juris:         juris     || 'Unknown',
    verdict:       result.overall_verdict      || '',
    risk:          result.risk_score           ?? null,
    entropy:       result.entropy_score        ?? null,
    inertia:       result.inertia_score        ?? null,
    motive:        result.motive_power         ?? null,
    legal_weight:  result.legal_weight_score   ?? null,
    investability: result.investability_score  ?? null,
    pressure_src:  result.pressure_source      || null,
    anomaly_count: (result.anomalies || []).length,
    high_count:    (result.anomalies || []).filter(a => a.severity === 'high').length,
    categories:    [...new Set((result.anomalies || []).map(a => a.category))],
    erga_omnes:    result.erga_omnes_effect     || null,
    // Share price data
    share_price:   shareData?.price            ?? null,
    market_cap:    shareData?.marketCap        ?? null,
    pe_ratio:      shareData?.peRatio          ?? null,
    pb_ratio:      shareData?.pbRatio          ?? null,
    lambda_div:    shareData?.lambdaDivergence ?? null,
  };
}
